// Standup history page — shows past standup summaries grouped by date.
// Each entry is a completed standup with summary text + action outcomes.

import { getSDK } from '../../../shared/functions/sdk.js'

export const TicketsStandupsMain = {
  extends: 'AppMain',
  padding: '0',

  Header: { extends: 'TicketsHeader' },

  Body: {
    overflow: 'auto',

    Hero: {
      extends: 'PageHero',
      eyebrow: 'STANDUPS',
      headline: 'Daily Standup History',
      lede: 'Your past standup summaries and action items.'
    },

    Content: {
      flow: 'y',
      gap: 'B',
      padding: 'B B1 D B1',
      maxWidth: 'L+L',
      width: '100%',
      margin: '0 auto',

      Empty: {
        hide: (el, s) => (s.root?._dailyUpdates || []).length > 0,
        padding: 'C',
        align: 'center center',
        color: 'caption',
        fontSize: 'Z2',
        text: 'No standup history yet. Submit your first daily update!'
      },

      childExtends: 'StandupDayCard',
      childrenAs: 'state',
      children: (el, s) => s.root?._dailyUpdates || []
    },

    // Load standup history on mount
    onCreate: async (el, s) => {
      const root = s.root
      if (!root) return
      const email = root.currentUserEmail
      if (!email) return

      try {
        // migrated 2026-04-27 to SDK route workspaceProject.standups list
        const result = await getSDK().execute('workspaceProject.standups', 'list', {
          filter: {
            author: email,
            select: 'date, update_text, summary, actions, created_at'
          },
          order: { column: 'date', ascending: false },
          limit: 30
        })
        const data = Array.isArray(result?.data) ? result.data : (result?.data || result || [])

        if (data?.length) {
          const updates = data.map(row => ({
            _date: row.date,
            _summary: row.summary || '',
            _userUpdate: row.update_text || '',
            _actions: row.actions || [],
            _createdAt: row.created_at
          }))
          root.update({ _dailyUpdates: updates })
        }
      } catch {}
    }
  }
}

// One standup entry — collapsible card with summary + actions
export const StandupDayCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'A',
  background: 'overlay',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',

  Header: {
    flow: 'x',
    align: 'center space-between',
    cursor: 'pointer',
    onClick: (e, el, s) => {
      const key = '_standupOpen_' + (s._createdAt || s._date)
      s.root.update({ [key]: !s.root[key] })
    },

    Left: {
      flow: 'x',
      align: 'center left',
      gap: 'Z',

      Icon: { extends: 'Icon', name: 'check', color: 'green', boxSize: 'A' },

      DateTitle: {
        tag: 'h3',
        margin: '0',
        fontSize: 'A',
        fontWeight: '600',
        color: 'title',
        text: (el, s) => {
          if (!s._date) return ''
          const d = new Date(s._date + 'T00:00:00')
          const dateStr = d.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })
          // Add time if multiple standups on same day
          if (s._createdAt) {
            const t = new Date(s._createdAt)
            const time = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
            return `${dateStr} · ${time}`
          }
          return dateStr
        }
      }
    },

    Stats: {
      flow: 'x',
      gap: 'Z',
      align: 'center right',

      ActionCount: {
        hide: (el, s) => !(s._actions || []).length,
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '600',
        color: 'green',
        text: (el, s) => {
          const confirmed = (s._actions || []).filter(a => a.status === 'confirmed').length
          return confirmed ? `${confirmed} applied` : ''
        }
      },

      Caret: {
        extends: 'Icon',
        name: 'chevronDown',
        boxSize: 'A',
        color: 'caption',
        transition: 'transform 120ms ease',
        transform: (el, s) => s.root?.['_standupOpen_' + (s._createdAt || s._date)] ? 'rotate(0)' : 'rotate(-90deg)'
      }
    }
  },

  // Collapsed preview — first line of summary
  Preview: {
    hide: (el, s) => !!s.root?.['_standupOpen_' + (s._createdAt || s._date)],
    tag: 'p',
    margin: '0',
    fontSize: 'Z2',
    color: 'caption',
    lineHeight: '1.4',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => {
      const raw = s._summary || ''
      // Strip markdown formatting for preview
      return raw.replace(/\*\*/g, '').replace(/\n/g, ' ').slice(0, 120)
    }
  },

  // Expanded detail
  Detail: {
    show: (el, s) => !!s.root?.['_standupOpen_' + (s._createdAt || s._date)],
    flow: 'y',
    gap: 'Z1',

    // User's original update
    UserUpdate: {
      hide: (el, s) => !s._userUpdate,
      flow: 'y',
      gap: 'V',
      padding: 'Z1 A',
      round: 'Z1',
      background: 'blue.04',
      borderWidth: '1px',
      borderColor: 'blue.1',
      borderStyle: 'solid',

      Label: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'blue',
        text: 'YOUR UPDATE'
      },
      Body: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'title',
        lineHeight: '1.5',
        whiteSpace: 'pre-wrap',
        text: (el, s) => s._userUpdate || ''
      }
    },

    // AI summary
    Summary: {
      flow: 'y',
      gap: 'V',

      Label: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'purple',
        text: 'AI SUMMARY'
      },
      Body: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        color: 'title',
        lineHeight: '1.6',
        whiteSpace: 'pre-wrap',
        text: (el, s) => s._summary || ''
      }
    },

    // Action outcomes
    Actions: {
      hide: (el, s) => !(s._actions || []).length,
      flow: 'y',
      gap: 'V',

      Label: {
        tag: 'span',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption',
        text: 'ACTIONS'
      },

      List: {
        flow: 'y',
        gap: 'V',
        childExtends: 'StandupHistoryAction',
        childrenAs: 'state',
        children: (el, s) => (s._actions || []).map((a, i) => ({
          ...a,
          _idx: i
        }))
      }
    }
  }
}

// One action outcome in history
export const StandupHistoryAction = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'V 0',

  StatusIcon: {
    tag: 'span',
    fontSize: 'Z2',
    text: (el, s) => s.status === 'confirmed' ? '✓' : '—',
    color: (el, s) => s.status === 'confirmed' ? 'green' : 'caption'
  },

  TypeBadge: {
    tag: 'span',
    fontSize: 'Y1',
    fontWeight: '700',
    letterSpacing: '.03em',
    padding: 'V Z',
    round: 'V',
    background: (el, s) => {
      if (s.status === 'skipped') return 'white.04'
      if (s.type === 'create_ticket') return 'orange.1'
      if (s.type === 'move_ticket') return 'blue.1'
      return 'yellow.1'
    },
    color: (el, s) => {
      if (s.status === 'skipped') return 'caption'
      if (s.type === 'create_ticket') return 'orange'
      if (s.type === 'move_ticket') return 'blue'
      return 'yellow'
    },
    text: (el, s) => {
      if (s.type === 'create_ticket') return 'CREATE'
      if (s.type === 'move_ticket') return 'MOVE'
      return 'UPDATE'
    }
  },

  Description: {
    tag: 'span',
    fontSize: 'Z2',
    color: (el, s) => s.status === 'skipped' ? 'caption' : 'title',
    textDecoration: (el, s) => s.status === 'skipped' ? 'line-through' : 'none',
    text: (el, s) => s.summary || s.title || s.result || ''
  }
}
